/// <reference types="node" />

export const BRAND_NAME = process.env.NEXT_PUBLIC_BRAND_NAME ?? "Brand";
export const SITE_DOMAIN = process.env.NEXT_PUBLIC_SITE_DOMAIN ?? "example.com";
export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL ?? `https://${SITE_DOMAIN}`;
export const API_BASE_URL = process.env.NEXT_PUBLIC_API_BASE_URL ?? "https://api.example.com";
export const S3_PUBLIC_URL = process.env.NEXT_PUBLIC_S3_PUBLIC_URL ?? "";
export const TG_SUPPORT_URL =
  process.env.NEXT_PUBLIC_BRAND_TELEGRAM_SUPPORT_URL ?? "https://t.me/support";
export const EXTENSION_DOWNLOAD_URL =
  process.env.NEXT_PUBLIC_EXTENSION_DOWNLOAD_URL ??
  "https://storage.yandexcloud.net/performance-s3/extension/kupikupi-extension-latest.zip";
export const EXTENSION_DOWNLOAD_URL_FIREFOX =
  process.env.NEXT_PUBLIC_EXTENSION_DOWNLOAD_URL_FIREFOX ??
  "https://storage.yandexcloud.net/performance-s3/extension/kupikupi-extension-firefox-latest.zip";
/** Empty string = not yet listed in the store; non-empty = open store page directly. */
export const EXTENSION_STORE_URL_CHROME = process.env.NEXT_PUBLIC_EXTENSION_STORE_URL_CHROME ?? "";
export const EXTENSION_STORE_URL_FIREFOX =
  process.env.NEXT_PUBLIC_EXTENSION_STORE_URL_FIREFOX ?? "";
export const CLIENT_TYPE_HEADER_KEY =
  process.env.NEXT_PUBLIC_BRAND_CLIENT_TYPE_HEADER ?? "X-Client-Type";
export const CLIENT_TYPE = process.env.NEXT_PUBLIC_BRAND_CLIENT_TYPE || "PC Web";
export const LOCALES = (process.env.NEXT_PUBLIC_LOCALES ?? "ru").split(",").filter(Boolean);
export const CURRENCIES = (process.env.NEXT_PUBLIC_CURRENCIES ?? "RUB").split(",").filter(Boolean);
/**
 * The first entry in `LOCALES` / `CURRENCIES` is the env's default.
 * Per-env policy (see `apps/web/.env.*`):
 *  - local dev: zh + CNY
 *  - staging:   ru + zh  /  RUB + CNY  (ru/RUB default)
 *  - prod:      ru + RUB
 */
export const DEFAULT_LOCALE = (LOCALES[0] ?? "ru") as "ru" | "zh" | "en";
export const DEFAULT_CURRENCY = (CURRENCIES[0] ?? "RUB") as "RUB" | "CNY" | "USD";
export const FEATURE_VK = process.env.NEXT_PUBLIC_FEATURE_VK === "1";
export const YM_COUNTER_ID = Number(process.env.NEXT_PUBLIC_YM_COUNTER_ID ?? "0") || 0;
export const MOCK_TG = process.env.NEXT_PUBLIC_MOCK_TG === "true";

// Yandex SmartCaptcha public site key (ysc1_…). Server-side validation runs
// against the matching ysc2_ secret on the Java backend; the browser widget
// only sees this public key. Empty string ⇒ widget renders a "local dev"
// bypass affordance via apps/web SmartCaptchaWidget (it never produces a
// real token, so SearchAbuseGuard will reject the retry — fine for local).
export const SMART_CAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_SMART_CAPTCHA_SITE_KEY ?? "";

function positiveIntFromEnv(value: string | undefined, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}

export interface ServicePolicyConfig {
  source: "env-default" | "api-ready";
  storage: {
    freePeriodDays: number;
    paidUnitDays: number;
    paidUnitPriceCnyFen: number | null;
    retentionPeriodDays: number | null;
  };
  inspectionPhotos: {
    freeCountMin: number;
    freeCountMax: number;
  };
  buyerAcceptance: {
    minHours: number;
    maxHours: number;
  };
}

const DEFAULT_FREE_INSPECTION_PHOTO_COUNT_MIN = positiveIntFromEnv(
  process.env.NEXT_PUBLIC_FREE_INSPECTION_PHOTO_COUNT_MIN,
  4,
);
const DEFAULT_FREE_INSPECTION_PHOTO_COUNT_MAX = Math.max(
  DEFAULT_FREE_INSPECTION_PHOTO_COUNT_MIN,
  positiveIntFromEnv(process.env.NEXT_PUBLIC_FREE_INSPECTION_PHOTO_COUNT_MAX, 6),
);
const DEFAULT_BUYER_ACCEPTANCE_MIN_HOURS = positiveIntFromEnv(
  process.env.NEXT_PUBLIC_BUYER_ACCEPTANCE_MIN_HOURS,
  1,
);
const DEFAULT_BUYER_ACCEPTANCE_MAX_HOURS = Math.max(
  DEFAULT_BUYER_ACCEPTANCE_MIN_HOURS,
  positiveIntFromEnv(process.env.NEXT_PUBLIC_BUYER_ACCEPTANCE_MAX_HOURS, 6),
);

export const SERVICE_POLICY_CONFIG: ServicePolicyConfig = {
  source: "env-default",
  storage: {
    freePeriodDays: positiveIntFromEnv(process.env.NEXT_PUBLIC_FREE_STORAGE_DAYS, 60),
    paidUnitDays: positiveIntFromEnv(process.env.NEXT_PUBLIC_PAID_STORAGE_UNIT_DAYS, 1),
    paidUnitPriceCnyFen:
      process.env.NEXT_PUBLIC_PAID_STORAGE_UNIT_PRICE_CNY_FEN == null
        ? null
        : positiveIntFromEnv(process.env.NEXT_PUBLIC_PAID_STORAGE_UNIT_PRICE_CNY_FEN, 0),
    retentionPeriodDays:
      process.env.NEXT_PUBLIC_STORAGE_RETENTION_DAYS == null
        ? null
        : positiveIntFromEnv(process.env.NEXT_PUBLIC_STORAGE_RETENTION_DAYS, 0),
  },
  inspectionPhotos: {
    freeCountMin: DEFAULT_FREE_INSPECTION_PHOTO_COUNT_MIN,
    freeCountMax: DEFAULT_FREE_INSPECTION_PHOTO_COUNT_MAX,
  },
  buyerAcceptance: {
    minHours: DEFAULT_BUYER_ACCEPTANCE_MIN_HOURS,
    maxHours: DEFAULT_BUYER_ACCEPTANCE_MAX_HOURS,
  },
};

export const SERVICE_POLICY_QUERY_KEY = ["service-policy"] as const;
export const FREE_STORAGE_DAYS = SERVICE_POLICY_CONFIG.storage.freePeriodDays;
export const FREE_INSPECTION_PHOTO_COUNT_MIN = SERVICE_POLICY_CONFIG.inspectionPhotos.freeCountMin;
export const FREE_INSPECTION_PHOTO_COUNT_MAX = SERVICE_POLICY_CONFIG.inspectionPhotos.freeCountMax;
export const FREE_INSPECTION_PHOTO_COUNT_LABEL =
  FREE_INSPECTION_PHOTO_COUNT_MIN === FREE_INSPECTION_PHOTO_COUNT_MAX
    ? String(FREE_INSPECTION_PHOTO_COUNT_MIN)
    : `${FREE_INSPECTION_PHOTO_COUNT_MIN}-${FREE_INSPECTION_PHOTO_COUNT_MAX}`;

/** China → Russia standard transit time, in days. Used across home / legal /
 *  tariffs / beginner-guide / business pages via the `{days}` i18n placeholder. */
export const TRANSIT_DAYS = Number(process.env.NEXT_PUBLIC_TRANSIT_DAYS ?? "21") || 21;

/**
 * Consolidation-warehouse receiving address shown to the customer so
 * they can give it to the CN seller. The customer-facing API does NOT expose a
 * warehouse-address endpoint (the backend only has STORAGE_* numeric sys
 * configs), so this is operations-owned config surfaced via env. The address is
 * written in Chinese on purpose — it is meant to be copy-pasted to a Chinese
 * seller. Defaults are blank so a misconfigured env never ships a wrong address.
 */
export const WAREHOUSE_RECEIVER = process.env.NEXT_PUBLIC_WAREHOUSE_RECEIVER ?? "";
export const WAREHOUSE_PHONE = process.env.NEXT_PUBLIC_WAREHOUSE_PHONE ?? "";
export const WAREHOUSE_ADDRESS = process.env.NEXT_PUBLIC_WAREHOUSE_ADDRESS ?? "";
export const WAREHOUSE_POSTCODE = process.env.NEXT_PUBLIC_WAREHOUSE_POSTCODE ?? "";
/** Customer-code prefix prepended to the user id (e.g. "KK" → KK207914). */
export const CUSTOMER_CODE_PREFIX = process.env.NEXT_PUBLIC_CUSTOMER_CODE_PREFIX ?? "KK";

/** Build the per-user customer code the seller must label the parcel with. */
export function customerCode(userId: string | null | undefined): string {
  return userId ? `${CUSTOMER_CODE_PREFIX}${userId}` : CUSTOMER_CODE_PREFIX;
}

/**
 * Yandex CDN proxy hosts for source-platform product images (issue #826).
 *
 * The browser pulls search/product-result images from these near-Russia edge
 * proxies instead of hitting the source CDNs directly (faster + edge-cached).
 * The proxy is **transparent**: it forwards the original path unchanged, so
 * `cdn-img-tb.kupikupi.com/<path>` returns exactly what `img.alicdn.com/<path>`
 * would — only the host (and the appended thumbnail suffix) differs.
 *
 * Ops-owned via env so the rewrite can be flipped off per-env (set the matching
 * key to the source host to no-op the swap, or empty to disable that platform).
 * Defaults are the live `cdn-img-*.kupikupi.com` subdomains (rewrite ON).
 *
 * NOTE: any new `NEXT_PUBLIC_CDN_*` key read here must also be added to the Mini
 * app's Vite `define` allowlist (apps/mini/vite.config.ts) — Vite has no
 * `process` in the browser and string-replaces only the allowlisted keys.
 */
export const CDN_IMG_HOST_TAOBAO =
  process.env.NEXT_PUBLIC_CDN_IMG_HOST_TAOBAO ?? "cdn-img-tb.kupikupi.com";
export const CDN_IMG_HOST_1688 =
  process.env.NEXT_PUBLIC_CDN_IMG_HOST_1688 ?? "cdn-img-1688.kupikupi.com";
export const CDN_IMG_HOST_WEIDIAN =
  process.env.NEXT_PUBLIC_CDN_IMG_HOST_WEIDIAN ?? "cdn-img-wd.kupikupi.com";

export type LocaleCode = "ru" | "zh" | "en";
export type CurrencyCode = "RUB" | "CNY" | "USD";
